import { useState, useRef, useEffect, useMemo } from 'react'
import { useAppStore } from '@cya-routes/shared'
import { AuthModal } from './AuthModal'
import { getUserMemberships, getClub } from '../../services/firebase/clubs'
import { onAuthStateChange } from '../../services/firebase/auth'
import { useLocation } from 'react-router-dom'
import { useClubNotifications } from '../../hooks/useClubNotifications'
import { useAdminNotifications } from '../../hooks/useAdminNotifications'
import { BookmarksModal } from '../bookmarks/BookmarksModal'
import type { Membership, Club } from '../../services/firebase/clubs'

interface UserMenuProps {
  onLoadRoutes?: () => void
  isMobile?: boolean
}

export function UserMenu({ onLoadRoutes, isMobile = false }: UserMenuProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const [isBookmarksModalOpen, setIsBookmarksModalOpen] = useState(false)
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login')
  const [adminClubs, setAdminClubs] = useState<{id: string, name: string}[]>([])
  const menuRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  
  const { auth, signOut, setUserMemberships } = useAppStore()
  const { currentUser, isAuthenticated } = auth
  
  const isOnEditorPage = location.pathname === '/editor'
  
  // Extract admin club IDs for notification hook
  const adminClubIds = useMemo(() => {
    return adminClubs.map(club => club.id)
  }, [adminClubs])
  
  // Use club notifications hook
  const { pendingCounts, totalPendingCount } = useClubNotifications(adminClubIds)
  
  // Check if user is website admin
  const isWebsiteAdmin = () => currentUser?.role === 'admin'

  const canModerateHazards = () => {
    const role = currentUser?.role
    return role === 'admin' || role === 'moderator'
  }
  
  // Use admin notifications hook
  const { creationRequests, deletionRequests, totalPendingCount: adminPendingCount } = useAdminNotifications(isWebsiteAdmin())
  
  // Calculate combined notification count
  const combinedNotificationCount = useMemo(() => {
    return totalPendingCount + adminPendingCount
  }, [totalPendingCount, adminPendingCount])
  
  // Load user's admin clubs using Firebase Auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (firebaseUser) => {
      if (firebaseUser && firebaseUser.uid) {
        console.log('🔥 Firebase auth state changed - Loading admin clubs for user:', firebaseUser.uid, firebaseUser.email)
        try {
          const memberships = await getUserMemberships(firebaseUser.uid)
          console.log('User memberships:', memberships)
          
          // Set memberships in store for the ClubDetailsDrawer to access
          setUserMemberships(memberships)
          
          const adminMemberships = memberships.filter(membership => 
            membership.role === 'admin' && membership.status === 'approved'
          )
          console.log('Admin memberships:', adminMemberships)
          
          // Fetch club details for each admin membership
          const clubDetails = await Promise.all(
            adminMemberships.map(async (membership) => {
              console.log('Fetching club details for:', membership.clubId)
              const club = await getClub(membership.clubId)
              console.log('Club details:', club)
              return club ? { id: club.id, name: club.name } : null
            })
          )
          
          // Filter out null values and set admin clubs
          const validClubs = clubDetails.filter((club): club is {id: string, name: string} => club !== null)
          console.log('Valid admin clubs:', validClubs)
          setAdminClubs(validClubs)
        } catch (error) {
          console.error('Error loading admin clubs:', error)
        }
      } else {
        console.log('🔥 Firebase auth state: no user or no uid')
        setAdminClubs([])
      }
    })
    
    return () => unsubscribe()
  }, [])
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  
  const handleSignOut = () => {
    signOut()
    setIsMenuOpen(false)
  }
  
  const openAuthModal = (mode: 'login' | 'register') => {
    setAuthMode(mode)
    setIsAuthModalOpen(true)
  }
  
  if (!isAuthenticated || !currentUser) {
    return (
      <>
        <div className="flex items-center">
          <button
            onClick={() => openAuthModal('login')}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-emerald-500 transition-all duration-200"
          >
            Sign In
          </button>
        </div>
        
        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
          initialMode={authMode}
        />
      </>
    )
  }
  
  const displayNameParts = (currentUser.displayName || `${currentUser.firstName} ${currentUser.lastName}`)
    .trim()
    .split(' ')
    .filter(Boolean)
  const userInitials = (
    (displayNameParts[0]?.charAt(0) || currentUser.firstName?.charAt(0) || '') +
    (displayNameParts[1]?.charAt(0) || currentUser.lastName?.charAt(0) || '')
  ).trim().toUpperCase() || 'CR'
  
  if (isMobile) {
    // Mobile version - expand inline
    return (
      <>
        <div>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="flex items-center justify-between w-full px-3 py-2 text-sm text-gray-300 hover:text-emerald-400 hover:bg-gray-700 rounded-md"
          >
            <div className="flex items-center space-x-3">
              {currentUser.profilePhoto ? (
                <img
                  src={currentUser.profilePhoto}
                  alt={currentUser.displayName}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-medium text-xs">
                  {userInitials}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {currentUser.displayName}
                </p>
              </div>
            </div>
            <svg 
              className={`w-4 h-4 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {isMenuOpen && (
            <div className="mt-2 pl-4 space-y-1">
              {/* All menu items for mobile - same as desktop */}
              <a
                href="/settings"
                className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 rounded-md"
              >
                <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </a>
              
              {isAuthenticated && (
                <a
                  href="/editor"
                  className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 rounded-md"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create New Route
                </a>
              )}
              
              {isAuthenticated && (
                <button
                  onClick={() => {
                    if (onLoadRoutes) {
                      onLoadRoutes()
                    } else {
                      window.location.href = '/editor'
                    }
                    setIsMenuOpen(false)
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 rounded-md"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  </svg>
                  Load Routes
                </button>
              )}
              
              {isAuthenticated && (
                <button
                  onClick={() => {
                    setIsBookmarksModalOpen(true)
                    setIsMenuOpen(false)
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 rounded-md"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                  My Bookmarks
                </button>
              )}
              
              {canModerateHazards() && (
                <a
                  href="/admin/hazards"
                  className="flex items-center w-full px-4 py-2 text-sm text-amber-300 hover:bg-gray-700 rounded-md"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.29 3.86L1.82 18a1.5 1.5 0 001.29 2.25h17.78A1.5 1.5 0 0022.18 18L13.71 3.86a1.5 1.5 0 00-2.42 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01" />
                  </svg>
                  Hazard moderation
                </a>
              )}

              {isWebsiteAdmin() && (
                <a
                  href="/admin"
                  className="flex items-center justify-between px-4 py-2 text-sm text-cyan-300 hover:bg-gray-700 rounded-md"
                >
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span>Website Admin</span>
                  </div>
                  {adminPendingCount > 0 && (
                    <div className="bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center font-medium">
                      {adminPendingCount}
                    </div>
                  )}
                </a>
              )}
              
              {adminClubs.length > 0 && (
                <div className="border-t border-gray-700 mt-1 pt-1">
                  <div className="px-4 py-2 text-xs font-medium text-gray-400 uppercase tracking-wide">
                    Club Admin
                  </div>
                  {adminClubs.map((club) => {
                    const pendingCount = pendingCounts[club.id] || 0
                    return (
                      <a
                        key={club.id}
                        href={`/club/${club.id}/manage`}
                        className="flex items-center justify-between px-4 py-2 text-sm text-emerald-300 hover:bg-gray-700 rounded-md"
                      >
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span>{club.name}</span>
                        </div>
                        {pendingCount > 0 && (
                          <div className="bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center font-medium">
                            {pendingCount}
                          </div>
                        )}
                      </a>
                    )
                  })}
                </div>
              )}
              
              {currentUser.stats.totalRoutes > 0 && (
                <div className="px-4 py-2 text-xs text-gray-400 border-t border-gray-700 mt-1">
                  {currentUser.stats.totalRoutes} routes • {currentUser.stats.clubMemberships} clubs
                </div>
              )}
              
              <div className="border-t border-gray-700 pt-1">
                <button
                  onClick={handleSignOut}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 rounded-md"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
        
        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
          initialMode={authMode}
        />
      </>
    )
  }

  return (
    <>
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="relative flex items-center hover:opacity-80 transition-opacity duration-200"
        >
          <div className="relative">
            {currentUser.profilePhoto ? (
              <img
                src={currentUser.profilePhoto}
                alt={currentUser.displayName}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                {userInitials}
              </div>
            )}
            {/* Notification Badge */}
            {combinedNotificationCount > 0 && (
              <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center font-medium animate-pulse">
                {combinedNotificationCount}
              </div>
            )}
          </div>
        </button>
        
        {isMenuOpen && (
          <div className="absolute right-0 mt-2 w-64 bg-gray-800/95 backdrop-blur-md rounded-md shadow-lg border border-gray-700/50 py-1 z-50">
            {/* User info */}
            <div className="px-4 py-3 border-b border-gray-700">
            <div className="flex items-center space-x-3">
              {currentUser.profilePhoto ? (
                <img
                  src={currentUser.profilePhoto}
                  alt={currentUser.displayName}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white font-medium">
                  {userInitials}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {currentUser.displayName}
                </p>
                <p className="text-sm text-gray-400 truncate">
                  @{currentUser.username}
                </p>
              </div>
            </div>
            </div>
            
            {/* Menu items */}
            <div className="py-1">
              <a
                href="/settings"
                className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
                onClick={() => setIsMenuOpen(false)}
              >
                <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </a>
              
              {/* Create New Route - always show for authenticated users */}
              {isAuthenticated && (
                <a
                  href="/editor"
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create New Route
                </a>
              )}
              
              {/* Load Routes - always show for authenticated users */}
              {isAuthenticated && (
                <button
                  onClick={() => {
                    if (onLoadRoutes) {
                      onLoadRoutes()
                    } else {
                      // If no handler provided, navigate to editor
                      window.location.href = '/editor'
                    }
                    setIsMenuOpen(false)
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  </svg>
                  Load Routes
                </button>
              )}
              
              {/* My Bookmarks - show for authenticated users */}
              {isAuthenticated && (
                <button
                  onClick={() => {
                    setIsBookmarksModalOpen(true)
                    setIsMenuOpen(false)
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                  My Bookmarks
                </button>
              )}
              
              {canModerateHazards() && (
                <a
                  href="/admin/hazards"
                  className="flex items-center px-4 py-2 text-sm text-amber-300 hover:bg-gray-700"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.29 3.86L1.82 18a1.5 1.5 0 001.29 2.25h17.78A1.5 1.5 0 0022.18 18L13.71 3.86a1.5 1.5 0 00-2.42 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01" />
                  </svg>
                  Hazard moderation
                </a>
              )}

              {/* Website Admin */}
              {isWebsiteAdmin() && (
                <a
                  href="/admin"
                  className="flex items-center justify-between px-4 py-2 text-sm text-cyan-300 hover:bg-gray-700 group"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span>Website Admin</span>
                  </div>
                  {adminPendingCount > 0 && (
                    <div className="bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center font-medium group-hover:bg-red-600">
                      {adminPendingCount}
                    </div>
                  )}
                </a>
              )}
              
              {/* Club Admin Links */}
              {adminClubs.length > 0 && (
                <div className="border-t border-gray-700 mt-1 pt-1">
                  <div className="px-4 py-2 text-xs font-medium text-gray-400 uppercase tracking-wide">
                    Club Admin
                  </div>
                  {adminClubs.map((club) => {
                    const pendingCount = pendingCounts[club.id] || 0
                    return (
                      <a
                        key={club.id}
                        href={`/club/${club.id}/manage`}
                        className="flex items-center justify-between px-4 py-2 text-sm text-emerald-300 hover:bg-gray-700 group"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span>{club.name}</span>
                        </div>
                        {pendingCount > 0 && (
                          <div className="bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center font-medium group-hover:bg-red-600">
                            {pendingCount}
                          </div>
                        )}
                      </a>
                    )
                  })}
                </div>
              )}
              
              {currentUser.stats.totalRoutes > 0 && (
                <div className="px-4 py-2 text-xs text-gray-400 border-t border-gray-700 mt-1">
                  {currentUser.stats.totalRoutes} routes • {currentUser.stats.clubMemberships} clubs
                </div>
              )}
            </div>
            
            {/* Sign out */}
            <div className="border-t border-gray-700 py-1">
              <button
                onClick={handleSignOut}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
              >
                <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign Out
              </button>
            </div>
          </div>
        )}
      </div>
      
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        initialMode={authMode}
      />
      
      <BookmarksModal
        isOpen={isBookmarksModalOpen}
        onClose={() => setIsBookmarksModalOpen(false)}
      />
    </>
  )
}
